/**
 * 訂單歷史 — localStorage 持久化（同 [[ticket-history]] / [[refund-history]] 模式）。
 *
 * Order Confirm 取得 OrderSummary 後 push 一筆；保留 state + total 供 list 顯示。
 * 不存 buyer PII；僅 order_id / state / total / addedAt。
 */

const STORAGE_KEY_PREFIX = 'boat:order-history:v1';
const MAX_ENTRIES = 50;

export interface OrderHistoryEntry {
  orderId: string;
  /** 寫入當下之 order.state（後續可能變化；list 頁面建議不依此判退） */
  state?: string;
  /** 寫入當下之 total（PostgreSQL numeric string） */
  total?: string;
  addedAt: string;
}

function storageKey(userId: string | undefined): string {
  return `${STORAGE_KEY_PREFIX}:${userId ?? 'anon'}`;
}

function safeRead(userId: string | undefined): OrderHistoryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is OrderHistoryEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as OrderHistoryEntry).orderId === 'string' &&
        typeof (e as OrderHistoryEntry).addedAt === 'string',
    );
  } catch {
    return [];
  }
}

function safeWrite(userId: string | undefined, entries: OrderHistoryEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(entries));
  } catch {
    // quota exceeded / disabled — silent fail
  }
}

export function listOrderHistory(userId: string | undefined): OrderHistoryEntry[] {
  return safeRead(userId).sort((a, b) => b.addedAt.localeCompare(a.addedAt));
}

export function rememberOrder(
  userId: string | undefined,
  entry: Omit<OrderHistoryEntry, 'addedAt'> & { addedAt?: string },
): void {
  const now = entry.addedAt ?? new Date().toISOString();
  const existing = safeRead(userId);
  const without = existing.filter((e) => e.orderId !== entry.orderId);
  const next: OrderHistoryEntry = {
    orderId: entry.orderId,
    addedAt: now,
    ...(entry.state !== undefined && { state: entry.state }),
    ...(entry.total !== undefined && { total: entry.total }),
  };
  const merged = [next, ...without].slice(0, MAX_ENTRIES);
  safeWrite(userId, merged);
}

export function clearOrderHistory(userId: string | undefined): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}
